/* eslint-disable prettier/prettier */
import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Button } from '../ui/Button';
import { FileText } from 'lucide-react';
import { FileType } from '../../types/FileType';
import { FileService } from '../../services/file.service';
import { FolderService } from '../../services/folder.service';
import { FolderType } from '../../types/FolderType';
import { CustomSelect } from '../ui/Select';

interface FileEditModalProps {
  file: FileType | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

export const FileEditModal = ({ file, isOpen, onClose, onSave }: FileEditModalProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>();
  const [folders, setFolders] = useState<FolderType[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [originalTitle, setOriginalTitle] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [originalFolderId, setOriginalFolderId] = useState<string | undefined>();

  useEffect(() => {
    if (file) {
      const titleVal = file.editedName || '';
      const descVal = file.description || '';
      const folderVal = file.folder?.id || undefined;

      setTitle(titleVal);
      setOriginalTitle(titleVal);

      setDescription(descVal);
      setOriginalDescription(descVal);

      setSelectedFolderId(folderVal);
      setOriginalFolderId(folderVal);
    }
  }, [file]);

  const [isFoldersLoaded, setIsFoldersLoaded] = useState(false);

  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const data = await FolderService.getAllWithHierarchy();
        setFolders(data);
        setIsFoldersLoaded(true);
      } catch (err) {
        console.error('Failed to load folders', err);
      }
    };

    if (isOpen) fetchFolders();
  }, [isOpen]);

  if (!isFoldersLoaded) {
    return null; 
  }

  const flattenFolders = (folders: FolderType[], prefix = ''): { label: string; value: string }[] =>
    folders.flatMap((folder) => {
      const label = prefix ? `${prefix} > ${folder.folderName}` : folder.folderName;
      const children = flattenFolders(folder.subfolders || [], label);
      return [{ label, value: folder.id }, ...children];
    });

  const folderOptions = flattenFolders(folders.filter((f) => f.id !== 'uncategorized'));

  const isUnchanged =
    title === originalTitle &&
    description === originalDescription &&
    selectedFolderId === originalFolderId;

  const handleSave = async () => {
    if (!file) return;
    setIsSubmitting(true);
    try {
      await FileService.editFile(file.id, {
        editedTitle: title,
        description,
        folderId: selectedFolderId,
      });
      onSave();
      onClose();
    } catch (err) {
      console.error('Edit failed', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit File Details">
      <div className="flex items-center justify-between w-full px-4 py-3 overflow-auto border mt-7 border-secondary rounded-xl">
        <div className="flex items-center gap-3 w-[95%]">
          <FileText className="w-6 h-6 font-semibold text-primary" />
          <p className="text-sm font-semibold truncate text-primary">{file?.fileName}</p>
        </div>
      </div>

      <Label className="mt-5 mb-1 text-lg font-semibold">Title</Label>
      <Input
        placeholder="Enter a title (optional)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <Label className="mt-4 mb-1 text-lg font-semibold">Description</Label>
      <Input
        placeholder="Enter a description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <Label className="mt-4 mb-1 text-lg font-semibold">Folder</Label>
      <CustomSelect
        placeholder="Select a folder (optional)"
        data={[{ label: 'None', value: '' }, ...folderOptions]}
        value={selectedFolderId ?? ''}
        onChange={(val) => setSelectedFolderId(val || undefined)}
      />

      <div className="flex justify-center gap-2 mb-3 mt-9 sm:justify-end">
        <Button
          variant="secondary"
          onClick={onClose}
          className="px-7 sm:px-10"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="px-7 sm:px-10"
          disabled={isSubmitting || isUnchanged}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};
